/*
 * ARLanding.styles.js — ARGO · Design tokens + styled-components
 * Import vào ArgoScreen.jsx
 */
import styled, { keyframes, createGlobalStyle } from 'styled-components';

/* ── Design tokens ── */
export const T = {
  blue:   '#00C2FF',
  purple: '#7B61FF',
  navy:   '#081B3A',
  white:  '#FFFFFF',
  bg:     '#020817',
};

/* ── Keyframes ── */
export const spin = keyframes`to { transform: rotate(360deg); }`;

const rippleOut = keyframes`
  0%   { box-shadow: 0 0 0 0   rgba(0,194,255,.5), 0 0 0 0   rgba(123,97,255,.3); }
  70%  { box-shadow: 0 0 0 20px rgba(0,194,255,0),  0 0 0 36px rgba(123,97,255,0); }
  100% { box-shadow: 0 0 0 0   rgba(0,194,255,0),  0 0 0 0   rgba(123,97,255,0); }
`;

export const GlobalFonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@600;700;800&family=Montserrat:wght@400;500;600;700&display=swap');
`;

/* ── Layout ── */
export const Body = styled.div`
  min-height: 100dvh;
  min-height: -webkit-fill-available;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 50% 25%, rgba(0,194,255,.18), transparent 28%),
    radial-gradient(circle at 58% 54%, rgba(123,97,255,.2), transparent 34%),
    linear-gradient(180deg, #030817 0%, #081B3A 55%, #030817 100%);
`;

export const Screen = styled.main`
  position: relative;
  width: min(100vw, 430px);
  height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  isolation: isolate;
  font-family: Montserrat, Arial, sans-serif;
  color: ${T.white};
  background:
    linear-gradient(180deg, rgba(8,27,58,.08), rgba(3,8,23,.35)),
    radial-gradient(circle at 50% 42%, rgba(0,194,255,.14), transparent 42%);
`;

/* ── Brand — vùng 1 ── */
export const Brand = styled.header`
  height: 18%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 3;
  padding: 0 16px;
`;

export const Logo = styled.img`
  max-height: 60px;
  width: auto;
  object-fit: cover;
`;

/* ── Scan area — vùng 2, fill hết phần còn lại ── */
export const ScanArea = styled.section`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0 16px;
  margin: 0px 20px;
`;

export const Corners = styled.div`
  position: absolute;
  inset: 0;
  opacity: .96;
  z-index: 2;
`;

export const Corner = styled.span`
  position: absolute;
  width: 72px; height: 72px;
  border-color: transparent;
  &::before, &::after {
    content: "";
    position: absolute;
    background: linear-gradient(90deg, ${T.blue}, ${T.purple});
    box-shadow: 0 0 18px rgba(0,194,255,.8);
    border-radius: 9px;
  }
  &::before { height: 7px; width: 66px; }
  &::after  { width: 7px; height: 66px; }
  &.tl { left: 0; top: 0; }
  &.tl::before, &.tl::after { left: 0; top: 0; }
  &.tr { right: 0; top: 0; }
  &.tr::before, &.tr::after { right: 0; top: 0; }
  &.bl { left: 0; bottom: 0; }
  &.bl::before, &.bl::after { left: 0; bottom: 0; }
  &.br { right: 0; bottom: 0; }
  &.br::before, &.br::after { right: 0; bottom: 0; }
`;

/* Video feed — lùi vào đúng bằng độ dày thanh góc (7px)
 * để thanh góc nằm đè lên mép ngoài video */
export const VideoFeed = styled.div`
  position: absolute;
  inset: 7px;
  overflow: hidden;
  border-radius: 4px;
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: opacity 0.5s ease;
  z-index: 1;

  video {
    width: 100%; 
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &::after {
    content: ${({ $scanning }) => $scanning ? '""' : 'none'};
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${T.blue}, transparent);
    box-shadow: 0 0 12px ${T.blue};
    animation: scanLine 2s ease-in-out infinite;
    z-index: 2;
  }
  @keyframes scanLine {
    0%   { top: 0%;   opacity: 0; }
    10%  { opacity: 1; }
    90%  { opacity: 1; }
    100% { top: 100%; opacity: 0; }
  }
`;

/* Portal — ẩn khi đang scan */
export const Portal = styled.div`
  position: relative;
  width: min(min(80vw, 320px), 42vh);
  height: min(min(80vw, 320px), 42vh);
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: radial-gradient(circle, rgba(0,194,255,.08) 0 28%, transparent 54%);
  opacity: ${({ $hidden }) => $hidden ? 0 : 1};
  pointer-events: ${({ $hidden }) => $hidden ? 'none' : 'auto'};
  transition: opacity 0.4s ease;
  z-index: 2;

  &::before {
    content: "";
    position: absolute; 
    inset: 0; 
    border-radius: 50%;
    background: conic-gradient(from 0deg, ${T.blue}, #2088ff, ${T.purple}, ${T.blue});
    filter: drop-shadow(0 0 22px rgba(0,194,255,.8));
    animation: ${spin} 9s linear infinite;
    mask: radial-gradient(circle, transparent 56%, #000 58%, #000 62%, transparent 65%);
  }
  &::after {
    content: "";
    position: absolute; 
    inset: 15%; 
    border-radius: 50%;
    background: repeating-radial-gradient(circle, rgba(0,194,255,.22) 0 1px, transparent 2px 17px);
    opacity: .65;
  }
`;

export const Beam = styled.div`
  position: absolute;
  width: 2px; height: 78%;
  background: linear-gradient(180deg, transparent, ${T.blue}, transparent);
  box-shadow: 0 0 14px ${T.blue};
  opacity: .38; 
  z-index: 1;
`;

export const MarkA = styled.img`
  position: relative; 
  z-index: 2;
  max-width: 48%;
`;

/* ── Content — vùng 3 ── */
export const Content = styled.section`
  height: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  z-index: 3;
  padding: 0px 16px;
  gap: 4px;
  margin-bottom: 10px;
`;

export const Heading = styled.h1`
  font-family: 'Exo 2', sans-serif;
  font-size: clamp(22px, 6vw, 36px);
  font-weight: 800; letter-spacing: .08em;
  background: linear-gradient(90deg, ${T.blue}, ${T.purple});
  -webkit-background-clip: text; 
  background-clip: text; 
  color: transparent;
  text-transform: uppercase;
`;

export const Sub = styled.p`
  max-width: 320px;
  font-size: 15px; 
  line-height: 1.45;
  letter-spacing: .02em; 
  text-align: center;
  color: rgba(255,255,255,.85); 
  font-weight: 500;
  margin-bottom: 15px;
`;

export const Divider = styled.div`
  width: 120px; 
  height: 1px;
  background: linear-gradient(90deg, transparent, ${T.blue}, #fff, ${T.purple}, transparent);
  box-shadow: 0 0 10px ${T.blue};
`;

export const CTABtn = styled.button`
  appearance: none; 
  border: 0; 
  background: transparent; 
  color: #fff;
  display: inline-grid; 
  place-items: center;
  width: 68px; 
  height: 68px; 
  border-radius: 50%;
  position: relative; 
  cursor: pointer; 
  isolation: isolate;
  flex-shrink: 0;
  animation: ${rippleOut} 2s ease-out infinite;

  &::before {
    content: ""; 
    position: absolute; 
    inset: 0; 
    border-radius: 50%;
    background: conic-gradient(${T.blue}, ${T.purple}, ${T.blue});
    box-shadow: 0 0 24px rgba(0,194,255,.45);
    animation: ${spin} 5s linear infinite;
    z-index: -2;
  }
  &::after {
    content: ""; 
    position: absolute; 
    inset: 4px; 
    border-radius: 50%;
    background: rgba(8,27,58,.92); 
    z-index: -1;
  }
`;

export const CTAWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const ScanIcon = styled.span`
  width: 32px; 
  height: 32px;
  border: 3px solid transparent; 
  border-radius: 8px;
  position: relative;
  background:
    linear-gradient(${T.navy}, ${T.navy}) padding-box,
    linear-gradient(135deg, ${T.blue}, ${T.purple}) border-box;
  box-shadow: 0 0 15px rgba(0,194,255,.55);

  /* Vòng tròn tỏa ra ngoài */
  &::before, &::after {
    content: ""; 
    position: absolute;
    background: linear-gradient(90deg, ${T.blue}, ${T.purple});
    border-radius: 4px;
  }
  &::before {
    height: 3px; 
    width: 38px;
    left: 50%; 
    top: 50%; 
    transform: translate(-50%, -50%);
    box-shadow: 0 0 10px ${T.blue};
  }
  &::after {
    width: 3px; 
    height: 38px;
    left: 50%; 
    top: 50%; 
    transform: translate(-50%, -50%);
    box-shadow: 0 0 10px ${T.purple};
  }
`;

export const Status = styled.div`
  display: flex; 
  justify-content: center; 
  align-items: center; 
  gap: 8px;
  font-size: 10px; 
  letter-spacing: .08em;
  color: rgba(255,255,255,.7);
  text-transform: uppercase; 
  font-weight: 600; 
  white-space: nowrap;
  margin-top: 15px;
`;

export const LockIcon = styled.span`
  width: 14px; 
  height: 11px;
  border: 2px solid rgba(255,255,255,.8); 
  border-radius: 2px;
  position: relative; 
  flex-shrink: 0;
  &::before {
    content: ""; 
    position: absolute;
    width: 8px; 
    height: 7px;
    border: 2px solid rgba(255,255,255,.8); 
    border-bottom: 0;
    border-radius: 8px 8px 0 0;
    left: 50%; 
    top: -8px; 
    transform: translateX(-50%);
  }
`;

/* ── AR status overlay (hiện khi scanning) ── */
export const StopIcon = styled.span`
  width: 22px; 
  height: 22px;
  border-radius: 5px;
  background: linear-gradient(135deg, ${T.blue}, ${T.purple});
  box-shadow: 0 0 12px rgba(0,194,255,.6);
`;

export const ARChip = styled.div`
  display: inline-flex; 
  align-items: center; 
  gap: 8px;
  padding: 7px 14px; 
  border-radius: 999px;
  background: rgba(8,27,58,.65);
  backdrop-filter: blur(12px);
  border: 0.5px solid rgba(0,194,255,.3);
  font-size: 12px; font-weight: 600;
  letter-spacing: .05em;
  margin-bottom: 8px;
`;

export const ARDot = styled.span`
  width: 7px; 
  height: 7px; 
  border-radius: 50%;
  background: ${({ $found }) => $found ? '#00C2FF' : '#7B61FF'};
  box-shadow: 0 0 0 4px ${({ $found }) => $found ? 'rgba(0,194,255,.25)' : 'rgba(123,97,255,.25)'};
  animation: ${spin} ${({ $found }) => $found ? 'none' : '2s'} linear infinite;
`;
